import {
  bindable,
  bindingMode,
  ComponentAttached,
  ComponentDetached,
  customElement,
  inject,
  LogManager,
  observable,
} from "aurelia-framework";
import { I18N } from "aurelia-i18n";
import "jquery";
import "select2";
import "select2/dist/css/select2.min.css";
import { SelectItem } from "./select-item";

// extend global namespace with select2() function
// see https://github.com/Microsoft/TypeScript/issues/7148
declare global {
  interface JQuery {
    select2(cmdOrOptions?: string | any): JQuery;
  }
}

type AsyncSearchFunction = (searchOptions) => Promise<SelectItem[]>;

type Source = SelectItem[] | AsyncSearchFunction;

let log = LogManager.getLogger("k-select2");

@customElement("k-select2")
@inject(I18N)
export class KSelect2 implements ComponentAttached, ComponentDetached {
  /** name/id of custom select */
  @bindable name?: string;

  /**
   * source is either an array of objects with id/text properties, or a
   * function returning a Promise for that.
   */
  @bindable
  @observable({ changeHandler: "sourceChanged" })
  source: Source = [];

  /** selected value */
  @bindable({ defaultBindingMode: bindingMode.twoWay })
  @observable({ changeHandler: "selectedChanged" })
  selected?: SelectItem;

  @bindable placeholder: string = "";
  @bindable disabled: boolean = false;
  @bindable clearable: boolean = false;
  @bindable autoselectSingleElement: boolean = false;

  element: HTMLElement;

  constructor(private i18n: I18N) {}

  sourceChanged(newSource: Source, oldSource?: Source): void {
    if (!newSource) {
      if (this.autoselectSingleElement) {
        $(this.element).val("clear");
        $(this.element).trigger("change.select2");
      }
      return;
    }
    let oldSelection = $(this.element).val();
    this.detached();
    this.attached(true);
    if (typeof newSource === "function") return;
    let newSelection = (newSource as Array<SelectItem>).filter(
      (e) => e.selected,
    );
    if (newSelection.length > 0) {
      $(this.element).val(newSelection[0].id);
      $(this.element).trigger("change.select2");
    } else if (oldSelection && oldSelection != "-1") {
      $(this.element).val(oldSelection);
      $(this.element).trigger("change.select2");
    }
  }

  selectedChanged(newValue: SelectItem, oldValue?: SelectItem): void {
    if (!newValue || !newValue.id || newValue.id == "-1") {
      $(this.element).val("-1");
    } else {
      $(this.element).val(newValue.id);
    }
    $(this.element).trigger("change.select2");
  }

  attached(reattach?: boolean) {
    let self = this;

    // remove all existing <option> elements
    $(this.element).empty();
    // add default option for empty selection
    $(this.element).append(
      $("<option/>", {
        value: "-1",
        text: "",
      }),
    );
    // add selected value if available
    if (this.selected && !reattach) {
      $(this.element).append(
        $("<option/>", {
          value: this.selected.id,
          text: this.selected.text,
          selected: true,
        }),
      );
    }

    let commonOptions = {
      theme: "bootstrap4",
      maximumInputLength: 100,
      placeholder: {
        id: "-1",
        text: this.placeholder,
      },
      // See select2/src/js/select2/i18n/en.js for a list of default language keys.
      // Note: inputTooLong, inputTooShort and maximumSelected intentionally omitted because unused here
      language: {
        errorLoading: () => this.i18n.tr("components.k-select2.errorLoading"),
        loadingMore: () => this.i18n.tr("components.k-select2.loadingMore"),
        noResults: () => this.i18n.tr("components.k-select2.noResults"),
        searching: () => this.i18n.tr("components.k-select2.searching"),
      },
    };

    // init select2 component
    let sel;
    if (typeof this.source === "function") {
      sel = $(this.element).select2({
        dropdownParent: $(this.element).parent(),
        ajax: {
          // replace select2 default ajax functionality with our own:
          transport: function (params, success, failure) {
            return self.query(params, success, failure);
          },
        },
        ...commonOptions,
      });
    } else {
      // source is assumed to be an array
      sel = $(this.element).select2({
        data: this.source,
        ...commonOptions,
      });
    }

    // preload selected values
    //sel.val(this.selected.id).trigger('change');

    // on any change update the selected values
    sel.on("change", (event) => {
      // don't propagate endlessly
      // see: http://stackoverflow.com/a/34121891/4354884
      if (event.originalEvent) return;
      // note: getting 'data' is deprecated since select2 4.0, but it
      // still works. It's the only way to get the full data object.
      let data = $(this.element).select2("data");
      let item = data[0];
      let newSelected;
      if (item == null || !item.id || item.id == "-1") {
        newSelected = undefined;
      } else {
        newSelected = this.cloneAndCleanupObject(item);
      }
      if (
        (this.selected === undefined && newSelected !== undefined) ||
        (this.selected !== undefined && newSelected === undefined) ||
        (this.selected !== undefined && this.selected.id != newSelected.id)
      ) {
        // user selected a different value
        this.selected = newSelected;
        // dispatch a change event to outside listeners
        let customEvent = new CustomEvent("change", {
          bubbles: true,
          detail: { newValue: this.selected },
        });
        this.element.dispatchEvent(customEvent);
      }
    });

    log.debug("select2 attached");
  }

  detached() {
    $(this.element).select2("destroy");
    log.debug("select2 detached");
  }

  private query(params, success, failure): any {
    // call the search function which returns a promise
    let searchString = params.data.term;
    log.debug(`calling async search function for '${searchString}' ...`);
    (this.source as AsyncSearchFunction)({ $search: searchString })
      .then((result) => {
        let newData = this.modifyResult(result);
        success(newData);
        let customEvent = new CustomEvent("asyncdataloaded", {
          bubbles: true,
          detail: { newValue: newData },
        });
        this.element.dispatchEvent(customEvent);
      })
      .catch((error) => {
        log.error("error: ", error);
        failure(error);
      });
    // select2 assumes that the query (aka 'transport') function returns
    // a jqXHR object which we don't have here, but the implementation only
    // cares if the return value has a 'status' property (see
    // select2/src/js/data/ajax.js). status==0 means that the request was
    // aborted. Any other status is ignored.
    return {};
  }

  /** select2 requires that the result looks like this:
   * <pre>
   * {
   *   'results': [ data... ],  // could be a partial result
   *   'totals': number         // total size, used for paging
   * }
   * </pre>
   * If <tt>result</tt> is an array, this function wraps it into the above
   * structure, otherwise it leaves it as is, assuming it already <i>has</i>
   * this kind of structure.
   */
  private modifyResult(result) {
    if (typeof result === "undefined" || result == null) {
      log.debug("got no result");
      return { totals: null };
    } else if (Array.isArray(result)) {
      if (result.length > 0) {
        log.debug(`got ${result.length} result(s)`);
      } else {
        log.debug("got empty result");
      }
      return {
        results: result.map((e) => this.cloneAndCleanupObject(e)),
        totals: result.length,
      };
    } else {
      log.debug("got result: ", result);
      return result;
    }
  }

  private cloneAndCleanupObject(selected) {
    if (selected === undefined || selected == null) {
      log.debug("selected: null");
      return null;
    }
    let clone = (<any>Object).assign({}, selected);
    delete clone.element;
    delete clone.title;
    delete clone.disabled;
    //delete clone.selected;
    delete clone._resultId;
    return clone;
  }
}

export {};
